import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';
import Pagination from '../components/Pagination';

const MACHINE_LABELS = {
  hook: 'フック',
  three_claw: '3本爪',
  two_claw: '2本爪',
  jewel_catcher: 'ジュエルキャッチャー',
  push: 'プッシャー',
  unknown: '不明',
};

const MACHINE_OPTIONS = [
  { value: '', label: '機種タイプ' },
  { value: 'hook', label: 'フック' },
  { value: 'three_claw', label: '3本爪' },
  { value: 'two_claw', label: '2本爪' },
  { value: 'jewel_catcher', label: 'ジュエルキャッチャー' },
  { value: 'push', label: 'プッシャー' },
  { value: 'unknown', label: '不明' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'ステータス' },
  { value: 'completed', label: '完了' },
  { value: 'pending', label: '処理中' },
  { value: 'failed', label: '失敗' },
  { value: 'deferred_budget', label: '予算保留' },
];

const SORT_OPTIONS = [
  { value: 'time_desc', label: '新しい順' },
  { value: 'time_asc', label: '古い順' },
  { value: 'confidence_desc', label: '信頼度 高→低' },
  { value: 'confidence_asc', label: '信頼度 低→高' },
];

const IMAGE_OPTIONS = [
  { value: '', label: '画像有無' },
  { value: 'yes', label: '画像あり' },
  { value: 'no', label: '画像なし' },
];

const STATUS_LABELS = {
  completed: '完了',
  pending: '処理中',
  failed: '失敗',
  deferred_budget: '予算保留',
};

function confidenceBadge(val) {
  if (val >= 0.8) return 'success';
  if (val >= 0.5) return 'warning';
  return 'danger';
}

export default function ScanList() {
  useAuthGuard();

  const [sp, setSp] = useSearchParams();
  const initQ = sp.get('q') || '';
  const initMachine = sp.get('machine_type') || '';
  const initStatus = sp.get('status') || '';
  const initHasImage = sp.get('has_image') || '';
  const initSortBy = sp.get('sort_by') || 'time_desc';

  const [keyword, setKeyword] = useState(initQ);
  const [queryKeyword, setQueryKeyword] = useState(initQ);
  const [machineType, setMachineType] = useState(initMachine);
  const [statusFilter, setStatusFilter] = useState(initStatus);
  const [hasImageFilter, setHasImageFilter] = useState(initHasImage);
  const [sortBy, setSortBy] = useState(initSortBy);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(null);
  const [nextCursor, setNextCursor] = useState('');
  const [cursorHistory, setCursorHistory] = useState([]);
  const [actionLoading, setActionLoading] = useState('');
  const [actionResult, setActionResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [reanalyzeLoading, setReanalyzeLoading] = useState('');

  useEffect(() => {
    const p = new URLSearchParams();
    if (queryKeyword) p.set('q', queryKeyword);
    if (machineType) p.set('machine_type', machineType);
    if (statusFilter) p.set('status', statusFilter);
    if (hasImageFilter) p.set('has_image', hasImageFilter);
    if (sortBy && sortBy !== 'time_desc') p.set('sort_by', sortBy);
    setSp(p, { replace: true });
  }, [queryKeyword, machineType, statusFilter, hasImageFilter, sortBy, setSp]);

  const fetchScans = useCallback((cur, q, mt, st, hi, sb) => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const url = new URL(`${API_BASE_URL}/admin/scans`);
        url.searchParams.set('limit', '20');
        const useOffset = sb && sb !== 'time_desc';
        if (useOffset) {
          // For non-default sort, use offset-based pagination
          const offsetMatch = cur && cur.match(/^offset:(\d+)$/);
          if (offsetMatch) {
            url.searchParams.set('offset', offsetMatch[1]);
          }
          url.searchParams.set('sort_by', sb);
        } else {
          if (cur) url.searchParams.set('cursor', cur);
        }
        if (q && q.trim()) url.searchParams.set('q', q.trim());
        if (mt) url.searchParams.set('machine_type', mt);
        if (st) url.searchParams.set('status', st);
        if (hi) url.searchParams.set('has_image', hi);

        const res = await apiFetch(url.toString(), { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const list = data.data || [];
        setItems(Array.isArray(list) ? list : []);
        setHasMore(data.meta?.hasMore ?? false);
        setTotal(data.meta?.total ?? null);
        setNextCursor(data.meta?.cursor ?? '');
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Network Error');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, []);

  useEffect(() => {
    return fetchScans(cursor, queryKeyword, machineType, statusFilter, hasImageFilter, sortBy);
  }, [cursor, queryKeyword, machineType, statusFilter, hasImageFilter, sortBy, fetchScans]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCursor('');
    setCursorHistory([]);
    setQueryKeyword(keyword.trim());
  };

  const handleClear = () => {
    setKeyword('');
    setQueryKeyword('');
    setMachineType('');
    setStatusFilter('');
    setHasImageFilter('');
    setSortBy('time_desc');
    setCursor('');
    setCursorHistory([]);
  };

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setCursor('');
    setCursorHistory([]);
  };

  const handleNext = () => {
    if (nextCursor) {
      setCursorHistory((prev) => [...prev, cursor]);
      setCursor(nextCursor);
    }
  };

  const handlePrev = () => {
    setCursorHistory((prev) => {
      const next = [...prev];
      const prevCursor = next.pop();
      setCursor(prevCursor || '');
      return next;
    });
  };

  const handleDeleteNoImage = async () => {
    if (!window.confirm('画像なしのスキャンデータを全て削除しますか？')) return;
    setActionLoading('no-image');
    setActionResult(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/scans/no-image`, { method: 'DELETE' });
      const data = await res.json();
      setActionResult({ type: 'success', msg: `${data.data?.deleted ?? 0} 件削除しました` });
      setCursor('');
      setCursorHistory([]);
      fetchScans('', queryKeyword, machineType, statusFilter, hasImageFilter, sortBy);
    } catch (e) {
      setActionResult({ type: 'danger', msg: e.message });
    } finally {
      setActionLoading('');
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('警告: 全てのスキャンデータが削除されます。よろしいですか？')) return;
    if (!window.confirm('最終確認です。本当に削除しますか？')) return;
    setActionLoading('reset');
    setActionResult(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/scans`, { method: 'DELETE' });
      const data = await res.json();
      setActionResult({ type: 'success', msg: `${data.data?.deleted ?? 0} 件削除しました` });
      setCursor('');
      setCursorHistory([]);
      fetchScans('', queryKeyword, machineType, statusFilter, hasImageFilter, sortBy);
    } catch (e) {
      setActionResult({ type: 'danger', msg: e.message });
    } finally {
      setActionLoading('');
    }
  };

  const handleReanalyze = async (scanId) => {
    setReanalyzeLoading(scanId);
    setActionResult(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/scans/${scanId}/reanalyze`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setActionResult({ type: 'success', msg: '再解析をキューしました' });
      fetchScans(cursor, queryKeyword, machineType, statusFilter, hasImageFilter, sortBy);
    } catch (e) {
      setActionResult({ type: 'danger', msg: `再解析に失敗: ${e.message}` });
    } finally {
      setReanalyzeLoading('');
    }
  };

  const handleReanalyzeAllFailed = async () => {
    if (!window.confirm('失敗・処理中のスキャンを全て再解析しますか？')) return;
    setActionLoading('reanalyze');
    setActionResult(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/scans/reanalyze-failed`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setActionResult({ type: 'success', msg: `${data.data?.enqueued ?? 0} 件の再解析をキューしました` });
      fetchScans(cursor, queryKeyword, machineType, statusFilter, hasImageFilter, sortBy);
    } catch (e) {
      setActionResult({ type: 'danger', msg: `再解析に失敗: ${e.message}` });
    } finally {
      setActionLoading('');
    }
  };

  const fmtDate = (s) => {
    if (!s) return '-';
    const d = new Date(s);
    if (isNaN(d)) return s;
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  };

  const hasActiveFilters = queryKeyword || machineType || statusFilter || hasImageFilter || (sortBy && sortBy !== 'time_desc');

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="page-title">スキャンデータ</h2>
          <p className="page-subtitle">スキャンデータの閲覧・検索・管理</p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={handleReanalyzeAllFailed}
            disabled={!!actionLoading}
            style={{ fontSize: '0.75rem' }}
          >
            {actionLoading === 'reanalyze' ? '処理中...' : '失敗を一括再解析'}
          </button>
          <button
            className="btn btn-outline-warning btn-sm"
            onClick={handleDeleteNoImage}
            disabled={!!actionLoading}
            style={{ fontSize: '0.75rem' }}
          >
            {actionLoading === 'no-image' ? '削除中...' : '画像なし削除'}
          </button>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={handleResetAll}
            disabled={!!actionLoading}
            style={{ fontSize: '0.75rem' }}
          >
            {actionLoading === 'reset' ? 'リセット中...' : '全件リセット'}
          </button>
        </div>
      </div>

      {actionResult && (
        <div className={`alert alert-${actionResult.type} alert-dismissible fade show`} role="alert">
          {actionResult.msg}
          <button type="button" className="btn-close" onClick={() => setActionResult(null)} />
        </div>
      )}

      {/* 検索・フィルタ */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <form onSubmit={handleSearch} className="d-flex align-items-center gap-2 flex-wrap">
            <div className="search-box" style={{ minWidth: '180px', maxWidth: '260px', flex: '1' }}>
              <i className="search-icon" data-feather="search" style={{ width: 14, height: 14 }}></i>
              <input
                type="search"
                className="form-control search-input"
                placeholder="景品名で検索"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
            <select
              className="form-select"
              value={machineType}
              onChange={handleFilterChange(setMachineType)}
              style={{ maxWidth: '160px', fontSize: '0.8125rem', height: '2.25rem' }}
            >
              {MACHINE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              className="form-select"
              value={statusFilter}
              onChange={handleFilterChange(setStatusFilter)}
              style={{ maxWidth: '140px', fontSize: '0.8125rem', height: '2.25rem' }}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              className="form-select"
              value={hasImageFilter}
              onChange={handleFilterChange(setHasImageFilter)}
              style={{ maxWidth: '140px', fontSize: '0.8125rem', height: '2.25rem' }}
            >
              {IMAGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              className="form-select"
              value={sortBy}
              onChange={handleFilterChange(setSortBy)}
              style={{ maxWidth: '160px', fontSize: '0.8125rem', height: '2.25rem' }}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button type="submit" className="btn btn-phoenix-primary btn-sm">検索</button>
            {hasActiveFilters && (
              <button type="button" className="btn btn-phoenix-secondary btn-sm" onClick={handleClear}>
                クリア
              </button>
            )}
          </form>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 60 }}>画像</th>
                <th>景品名</th>
                <th>メーカー</th>
                <th>機種</th>
                <th>信頼度</th>
                <th>秒数</th>
                <th>ステータス</th>
                <th>スキャン日時</th>
                <th style={{ width: 80 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="text-center py-4">読み込み中...</td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={9} className="text-danger py-4">{error}</td>
                </tr>
              )}
              {!loading && !error && items.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-4" style={{ color: '#8a94ad' }}>
                    {hasActiveFilters ? '条件に一致するデータがありません' : 'データがありません'}
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                items.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.imageUrl ? (
                        <img
                          src={s.imageUrl}
                          alt=""
                          className="scan-thumbnail"
                          style={{ width: 48, height: 48, objectFit: 'cover' }}
                          onClick={() => setPreviewUrl(s.imageUrl)}
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            width: 48, height: 48, borderRadius: 4,
                            backgroundColor: '#f5f7fa', color: '#8a94ad', fontSize: 10,
                          }}
                        >
                          なし
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="fw-medium">{s.prizeName || '-'}</span>
                    </td>
                    <td>
                      <span className="text-muted" style={{ fontSize: '0.8125rem' }}>
                        {s.prizeManufacturer || '-'}
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-secondary">
                        {MACHINE_LABELS[s.machineType] || s.machineType}
                      </span>
                    </td>
                    <td>
                      <span className={`badge bg-${confidenceBadge(s.machineConfidence)}`}>
                        {Math.round((s.machineConfidence || 0) * 100)}%
                      </span>
                    </td>
                    <td className="small text-muted text-nowrap">
                      {s.frameIndex != null ? `#${s.frameIndex}` : '-'}
                    </td>
                    <td>
                      <span className={`badge bg-${s.status === 'completed' ? 'success' : s.status === 'failed' ? 'danger' : s.status === 'deferred_budget' ? 'info' : 'warning'}`}>
                        {STATUS_LABELS[s.status] || s.status}
                      </span>
                    </td>
                    <td className="text-nowrap">{fmtDate(s.scannedAt)}</td>
                    <td>
                      {s.imageUrl && (s.status !== 'completed') && (
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => handleReanalyze(s.id)}
                          disabled={reanalyzeLoading === s.id}
                          style={{ fontSize: '0.6875rem', padding: '2px 8px' }}
                        >
                          {reanalyzeLoading === s.id ? '...' : '再解析'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="card-footer bg-white">
          <Pagination
            hasMore={hasMore}
            hasPrev={cursorHistory.length > 0}
            onNext={handleNext}
            onPrev={handlePrev}
            loading={loading}
            total={total}
            page={cursorHistory.length + 1}
            pageSize={20}
            itemCount={items.length}
          />
        </div>
      </div>

      {/* 画像プレビュー */}
      {previewUrl && (
        <div className="image-modal-overlay" onClick={() => setPreviewUrl(null)}>
          <img
            src={previewUrl}
            alt=""
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Layout>
  );
}
